import { useFormik } from "formik";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "store/hooks";
import { clearErrors, clearResetRequestSent, requestResetRequest } from "store/slices/authSlice";

import LoginTemplate from "modules/login/LoginTemplate";

import Button from "components/Button";
import TextInput from "components/TextInput";

import { QUERY_PARAMS } from "utils/constants";
import { QueryHelper } from "utils/helpers";
import { PAGES } from "utils/links";

import useValidation from "hooks/useValidation";

import { validationSchema } from "./validationSchema";

import InfoCard from "components/InfoCard";
import styles from "./style.module.scss";

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { isLoading, errors: authErrors, resetRequestSent } = useAppSelector((state) => state.auth);
  const formRef = useRef<HTMLFormElement>(null);
  const { step = "1" } = QueryHelper.getFromQueryString(location.search);

  const [submittedEmail, setSubmittedEmail] = useState("");
  const [cooldown, setCooldown] = useState(0);

  const form = useFormik({
    initialValues: {
      email: "",
    },
    onSubmit: (values) => {
      if (Number(step) === 1) {
        setSubmittedEmail(values.email);
        dispatch(requestResetRequest({ email: values.email }));
      }
    },
    validationSchema: validationSchema,
  });

  // Navigate to step 2 when reset request succeeds
  useEffect(() => {
    if (resetRequestSent && Number(step) === 1) {
      setCooldown(60);
      navigate(`${PAGES.RESET_PASSWORD}${QueryHelper.getQueryString({ [QUERY_PARAMS.STEP]: 2 })}`);
    }
  }, [resetRequestSent, step, navigate]);

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;

    const timer = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldown]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(clearResetRequestSent());
      dispatch(clearErrors());
    };
  }, [dispatch]);

  const handleTryAgain = useCallback(() => {
    if (cooldown > 0 || isLoading) return;

    dispatch(clearErrors());
    dispatch(clearResetRequestSent());
    dispatch(requestResetRequest({ email: submittedEmail }));
    setCooldown(60);
  }, [cooldown, isLoading, submittedEmail, dispatch]);

  useValidation(formRef, form.errors);

  return (
    <LoginTemplate
      title={step === "1" ? "Reset Password" : "Check your email"}
      description={
        step === "1" ? (
          `Enter your email address and\n we'll send you instructions to reset\n your password`
        ) : (
          <>
            We've sent password reset instructions to <span>{submittedEmail}</span>
          </>
        )
      }
    >
      <>
        {Number(step) === 1 && (
          <form ref={formRef} onSubmit={form.handleSubmit} className={styles.form}>
            <TextInput
              name="email"
              label="Email address"
              placeholder="your.email@company.com"
              value={form.values.email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (authErrors) dispatch(clearErrors());
                form.handleChange(e);
              }}
              error={form.errors.email || (typeof authErrors === "string" ? authErrors : undefined)}
            />

            <div className={styles.actionButtons}>
              <Button variant="primary" disabled={isLoading}>
                {isLoading ? "Sending..." : "Send instructions"}
              </Button>

              <Link to={PAGES.LOGIN}>
                <Button variant="secondary" type="button">
                  Back to login
                </Button>
              </Link>
            </div>
          </form>
        )}

        {Number(step) === 2 && (
          <div className={styles.steps}>
            <InfoCard className={styles.stepsList} variant="blue" color="blue" size="small">
              <ol>
                <p>Next steps:</p>

                <li>Check your inbox (and spam folder)</li>
                <li>Click the reset link in the email</li>
                <li>Create a new password</li>
              </ol>
            </InfoCard>

            {typeof authErrors === "string" ? (
              <p className={styles.apiError}>{authErrors}</p>
            ) : (
              <div className={styles.again}>
                <p>Didn't receive the email?</p>

                <Button variant="link" type="button" onClick={handleTryAgain} disabled={cooldown > 0 || isLoading}>
                  {cooldown > 0 ? `Try again (${cooldown})` : "Try again"}
                </Button>
              </div>
            )}

            <Link to={PAGES.LOGIN} className={styles.backToLogin}>
              <Button variant="secondary" type="button">
                Back to login
              </Button>
            </Link>
          </div>
        )}
      </>
    </LoginTemplate>
  );
};

export default ResetPasswordPage;
